import clsx from "clsx";
import { twMerge } from "tailwind-merge";

import { getCheckboxClassNames } from "./class-names";
import { Icon } from "../icon";

export type CheckboxProps = {
  label?: string;
  value?: string;
  title?: string;
  checked?: boolean;
  indeterminate?: boolean;
  onChange?: (checked: boolean, value: string | undefined) => void;
  onEventChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  dataTestId?: string;
  stopPropagation?: boolean;
};

export const Checkbox = ({
  label,
  value,
  title,
  checked,
  indeterminate = false,
  onChange,
  onEventChange,
  disabled = false,
  className,
  error = false,
  dataTestId,
  stopPropagation = false,
}: CheckboxProps) => (
  // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
  <label
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    tabIndex={0}
    title={title}
    className={clsx(
      "group inline-flex gap-small items-center outline-none hyphens-auto",
      disabled ? "cursor-not-allowed" : "cursor-pointer",
      className,
    )}
    onKeyDown={(e) => {
      if (e.key === " " || e.key === "Spacebar" || e.code === "Space") {
        e.preventDefault();
        if (!disabled) {
          onChange?.(!checked, value);
        }
      }
    }}
  >
    <input
      className="hidden"
      type="checkbox"
      disabled={disabled}
      checked={checked}
      onChange={(e) => {
        if (onChange) onChange(e.target.checked, value);
        else if (onEventChange) onEventChange(e);
      }}
      data-testid={dataTestId || "checkbox-input"}
      ref={(input) => {
        if (input) input.indeterminate = indeterminate;
      }}
      onClick={(e) => e.stopPropagation()}
    />
    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
    <div
      className={twMerge(
        getCheckboxClassNames({
          checked: checked || indeterminate,
          disabled,
          error,
        }),
      )}
      onClick={stopPropagation ? (e) => e.stopPropagation() : undefined}
    >
      {checked && !indeterminate && (
        <Icon
          name="solidCheck"
          className={clsx(
            "w-[1.2rem] h-[1.2rem] text-white",
            disabled && "text-neutral-400",
          )}
        />
      )}
      {indeterminate && (
        <Icon
          name="solidMinus"
          className={clsx(
            "w-[1.2rem] h-[1.2rem] text-white",
            disabled && "text-neutral-400",
          )}
        />
      )}
      <div
        className={twMerge(
          clsx(
            "hidden toggle-border absolute inset-[-0.4rem] border rounded-[6px] border-solid",
            !disabled && "group-focus:border-blue-560 group-focus:block",
            !disabled && error && "border-red-550 block",
          ),
        )}
      />
    </div>
    {label && (
      <span
        className={clsx("text-ui text-left", disabled && "text-neutral-400")}
      >
        {label}
      </span>
    )}
  </label>
);
